/** Discriminates the transport failure cause for messaging/telemetry. */
export type ApiTransportErrorKind = 'timeout' | 'network';

/**
 * Transport-level failure: the request never produced an HTTP response
 * (client-side timeout, offline, DNS, connection reset). Thrown by
 * `ApiClient` with a human-readable message so retry classifiers can
 * distinguish transient connectivity blips (safe to retry) from HTTP
 * 4xx/5xx application errors (which must never be retried) without
 * relying on fragile `instanceof` checks against built-ins like
 * `TypeError`/`DOMException` — those break as soon as the error is
 * wrapped. The original fetch rejection is preserved as `cause` so
 * DNS vs offline vs abort stays diagnosable.
 */
export class ApiTransportError extends Error {
  /** Marker consumed by retry classifiers: transport failures are transient. */
  readonly isTransient = true as const;
  readonly kind: ApiTransportErrorKind;

  constructor(kind: ApiTransportErrorKind, message: string, cause: unknown) {
    super(message, { cause });
    this.name = 'ApiTransportError';
    this.kind = kind;
  }
}

/**
 * Retry classifier guard: `true` only for transport-level failures thrown
 * by `ApiClient` (timeout/offline/DNS). HTTP 4xx/5xx errors — and any
 * non-transport throw — return `false`, so validation/permission failures
 * are never hammered with retries.
 */
export function isTransientApiError(error: unknown): error is ApiTransportError {
  return error instanceof ApiTransportError;
}

/**
 * Application-level HTTP failure: the request reached the backend and returned
 * a non-2xx status. Exposes the response status so callers can react to
 * specific codes (e.g. 409 Conflict) without sniffing the error message.
 */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Guard for HTTP errors thrown by ApiClient. Returns the error with its status
 * code when it is a typed HTTP failure; otherwise returns null so callers can
 * fall back to generic error handling.
 */
export function asApiHttpError(error: unknown): ApiHttpError | null {
  return error instanceof ApiHttpError ? error : null;
}

/**
 * Extract a renderable string from a JSON error body. NestJS validation
 * pipes can return `message` as a string, a `string[]`, or — when the
 * exceptionFactory is overridden — an array of `ValidationError` objects.
 * Rendering those objects directly inside React crashes with "Objects are
 * not valid as a React child", so this helper collapses everything down to
 * a single human-readable string.
 */
export function extractErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const msg = (body as { message?: unknown }).message;
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) {
    const parts = msg
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') {
          const constraints = (entry as { constraints?: Record<string, string> })
            .constraints;
          if (constraints) return Object.values(constraints).join('; ');
          const property = (entry as { property?: string }).property;
          if (property) return `Invalid value for "${property}".`;
        }
        return null;
      })
      .filter((p): p is string => Boolean(p));
    if (parts.length > 0) return parts.join(' ');
  }
  const error = (body as { error?: unknown }).error;
  if (typeof error === 'string') return error;
  return fallback;
}
